import Konva from 'konva';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlattenArea, FlattenShape, ShapeId } from 'store/slices/TreeSlice';
import PolygonHandle from './PolygonHandle';
import { circleToPolygonPoints, getCircleFromPolygonPoints, getCircleHandlePoints } from './flattenAreaToolUtils';

interface ActiveHandlesProps {
	polygon: FlattenArea;
	shape: FlattenShape;
	lineRefs: { current: Map<ShapeId, Konva.Line> };
	onCommit: (id: ShapeId, points?: number[]) => void;
	onSelect: (id: ShapeId) => void;
}

const MIN_RADIUS_PX = 2;

export default function ActiveHandles({ polygon, shape, lineRefs, onCommit, onSelect }: ActiveHandlesProps) {
	const points = polygon.coords;
	const flatPoints = useMemo(() => polygon.coords.flat(), [polygon.coords]);
	const [previewRadius, setPreviewRadius] = useState<number | null>(null);
	// Own copy of the points during a vertex drag — a stale re-render mid-drag can reset Konva's.
	const draggingIndexRef = useRef<number | null>(null);
	const editBufferRef = useRef<number[] | null>(null);
	const lastCirclePointsRef = useRef<number[] | null>(null);

	useEffect(() => {
		setPreviewRadius(null);
	}, [polygon.coords, polygon.id]);

	const getVertexPosition = useCallback((i: number, point: number[]) => {
		const buf = draggingIndexRef.current === i ? editBufferRef.current : null;
		return buf ? { x: buf[i * 2], y: buf[i * 2 + 1] } : { x: point[0], y: point[1] };
	}, []);

	const handleVertexDragStart = useCallback((i: number) => {
		draggingIndexRef.current = i;
		editBufferRef.current = flatPoints.slice();
	}, [flatPoints]);

	const handleVertexDragMove = useCallback((i: number, e: Konva.KonvaEventObject<DragEvent>) => {
		const line = lineRefs.current.get(polygon.id);
		const buffer = editBufferRef.current;
		if (!line || !buffer) return;
		buffer[i * 2] = e.target.x();
		buffer[i * 2 + 1] = e.target.y();
		line.points(buffer.slice());
		line.getLayer()?.batchDraw();
		e.cancelBubble = true;
	}, [lineRefs, polygon.id]);

	const handleVertexDragEnd = useCallback((e: Konva.KonvaEventObject<DragEvent>) => {
		e.cancelBubble = true;
		draggingIndexRef.current = null;
		const finalPoints = editBufferRef.current;
		editBufferRef.current = null;
		if (finalPoints) onCommit(polygon.id, finalPoints);
		e.evt?.preventDefault();
	}, [onCommit, polygon.id]);

	const handleVertexPointerDown = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		e.cancelBubble = true;
		onSelect(polygon.id);
	}, [onSelect, polygon.id]);

	if (shape === 'circle' && flatPoints.length >= 6) {
		const segments = Math.max(3, Math.floor(flatPoints.length / 2));
		const base = getCircleFromPolygonPoints(flatPoints);
		const circle = { ...base, radius: previewRadius ?? base.radius };
		const handles = getCircleHandlePoints(circle.cx, circle.cy, circle.radius);

		return (
			<>
				{handles.map((p, idx) => (
					<PolygonHandle
						key={`${polygon.id}-circle-handle-${idx}`}
						x={p.x}
						y={p.y}
						draggable
						onDragMove={(e) => {
							const line = lineRefs.current.get(polygon.id);
							if (!line) return;

							const nextRadius = Math.max(
								MIN_RADIUS_PX,
								Math.hypot(e.target.x() - base.cx, e.target.y() - base.cy)
							);
							setPreviewRadius(nextRadius);

							const nextPoints = circleToPolygonPoints(base.cx, base.cy, nextRadius, segments);
							lastCirclePointsRef.current = nextPoints;
							line.points(nextPoints);
							line.getLayer()?.batchDraw();

							const snapped = getCircleHandlePoints(base.cx, base.cy, nextRadius)[idx];
							e.target.position({ x: snapped.x, y: snapped.y });
							e.cancelBubble = true;
						}}
						onDragEnd={(e) => {
							e.cancelBubble = true;
							setPreviewRadius(null);
							onCommit(polygon.id, lastCirclePointsRef.current ?? undefined);
							lastCirclePointsRef.current = null;
							e.evt?.preventDefault();
						}}
						onPointerDown={(e) => {
							e.cancelBubble = true;
							onSelect(polygon.id);
						}}
					/>
				))}
			</>
		);
	}

	return (
		<>
			{points.map((point: number[], i: number) => {
				const pos = getVertexPosition(i, point);
				return (
					<PolygonHandle
						key={`${polygon.id}-handle-${i}`}
						x={pos.x}
						y={pos.y}
						draggable
						onDragStart={() => handleVertexDragStart(i)}
						onDragMove={(e) => handleVertexDragMove(i, e)}
						onDragEnd={handleVertexDragEnd}
						onPointerDown={handleVertexPointerDown}
					/>
				);
			})}
		</>
	);
}
